import { Component, ChangeDetectionStrategy, input, output, inject, signal, effect, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DialogModule } from 'primeng/dialog';
import { MenuModule } from 'primeng/menu';
import { Menu } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import { SaleDTO, SalePipelineStatus, SaleStatus, SaleType } from '../../services/sale.service';
import { InvoiceService } from '../../services/invoice.service';
import { apiFailureMessage, httpErrorApiMessage } from '../../core/api-response-utils';
import type { ChargeDTO, DelinquencyStatusEnumValue, InvoiceDTO } from '../../core/models';
import { CfdiInvoiceStatusEnum, CollectionMethodEnum } from '../../core/models';

@Component({
  selector: 'app-sale-read',
  templateUrl: './sale-read.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, FormsModule, DialogModule, MenuModule, ButtonModule, FloatLabelModule, InputTextModule]
})
export class SaleReadComponent {
  sale = input.required<SaleDTO>();
  charges = input<ChargeDTO[]>([]);
  openCustomer = output<number>();
  openSubscription = output<number>();

  private invoiceService = inject(InvoiceService);
  private sanitizer = inject(DomSanitizer);
  private toastr = inject(ToastrService);

  @ViewChild('invoiceActionsMenu') invoiceActionsMenu?: Menu;

  invoices = signal<InvoiceDTO[]>([]);
  invoicesLoading = signal(false);
  previewLoadingInvoiceId = signal<number | null>(null);
  downloadPdfLoadingInvoiceId = signal<number | null>(null);
  downloadXmlLoadingInvoiceId = signal<number | null>(null);
  invoiceRowMenuItems = signal<MenuItem[]>([]);
  cfdiPdfViewerVisible = signal(false);
  cfdiPdfViewerSafeUrl = signal<SafeResourceUrl | null>(null);
  resendCfdiDialogVisible = signal(false);
  resendCfdiEmail = signal('');
  resendCfdiLoading = signal(false);
  resendCfdiInvoiceId = signal<number | null>(null);
  chargeReceiptLoadingId = signal<number | null>(null);

  readonly CollectionMethodEnum = CollectionMethodEnum;

  private readonly processStatusOptions: { value: SaleStatus; label: string }[] = [
    { value: 'DRAFT', label: 'Borrador' },
    { value: 'ISSUED', label: 'Emitida' },
    { value: 'PARTIALLY_PAID', label: 'Parcialmente pagada' },
    { value: 'PAID', label: 'Pagada' },
    { value: 'CANCELLED', label: 'Cancelada' },
    { value: 'UNCOLLECTIBLE', label: 'Incobrable' }
  ];

  private readonly pipelineStatusOptions: { value: SalePipelineStatus; label: string }[] = [
    { value: 'ACCEPTED', label: 'Aceptado' },
    { value: 'CONTRACT_ATTACHMENT', label: 'Contrato/Anexo' },
    { value: 'OC', label: 'OC' },
    { value: 'INVOICED', label: 'Facturado' },
    { value: 'INVOICE_IMPORTED', label: 'Factura Ingresada' },
    { value: 'PAID', label: 'Pagado' },
    { value: 'LOST', label: 'Perdido' }
  ];

  private readonly saleTypeOptions: { value: SaleType; label: string }[] = [
    { value: 'ONE_TIME', label: 'Venta única' },
    { value: 'SUBSCRIPTION', label: 'Suscripción' }
  ];

  constructor() {
    effect(() => {
      const saleId = this.sale()?.id;
      if (saleId != null) {
        this.loadInvoices(saleId);
      } else {
        this.invoices.set([]);
      }
    });
  }

  getStatusLabel(status: SaleStatus | string | undefined): string {
    if (!status) return '—';
    return this.processStatusOptions.find((o) => o.value === status)?.label ?? String(status);
  }

  getDelinquencyLabel(d: DelinquencyStatusEnumValue | string | undefined): string {
    if (!d || d === 'CURRENT') return '';
    const map: Record<string, string> = { OVERDUE: 'Vencida' };
    return map[d] ?? d;
  }

  getPipelineStatusLabel(s: SalePipelineStatus | string | undefined): string {
    if (!s) return '—';
    return this.pipelineStatusOptions.find((o) => o.value === s)?.label ?? String(s);
  }

  getSaleTypeLabel(k: SaleType | string | undefined): string {
    if (!k) return '—';
    return this.saleTypeOptions.find((o) => o.value === k)?.label ?? String(k);
  }

  getChargeStatusLabel(status: string | undefined): string {
    if (!status) return '-';
    const labels: Record<string, string> = {
      SCHEDULED: 'Programado',
      PENDING: 'Pendiente',
      DUE: 'Vencido',
      PAID: 'Pagado',
      FAILED: 'Fallido',
      EXPIRED: 'Expirado',
      CANCELLED: 'Cancelado'
    };
    return labels[status] ?? status;
  }

  getChargeStatusBadgeClasses(status: string | undefined): string {
    const base = 'text-[10px] px-1.5 py-0.5 rounded shrink-0';
    switch (status) {
      case 'PAID':
        return `${base} bg-emerald-100 text-emerald-700`;
      case 'PENDING':
      case 'SCHEDULED':
        return `${base} bg-sky-100 text-sky-700`;
      case 'DUE':
      case 'EXPIRED':
        return `${base} bg-amber-100 text-amber-700`;
      case 'CANCELLED':
      case 'FAILED':
        return `${base} bg-red-100 text-red-700`;
      default:
        return `${base} bg-slate-100 text-slate-600`;
    }
  }

  getChargeStatusIcon(status: string | undefined): string {
    switch (status) {
      case 'PAID':
        return 'pi pi-check-circle';
      case 'PENDING':
      case 'SCHEDULED':
        return 'pi pi-clock';
      case 'DUE':
        return 'pi pi-exclamation-circle';
      case 'CANCELLED':
        return 'pi pi-times-circle';
      case 'FAILED':
        return 'pi pi-exclamation-triangle';
      case 'EXPIRED':
        return 'pi pi-ban';
      default:
        return 'pi pi-credit-card';
    }
  }

  getChargeStatusIconClasses(status: string | undefined): string {
    const base = 'shrink-0 h-7 w-7 rounded-full flex items-center justify-center';
    switch (status) {
      case 'PAID':
        return `${base} bg-emerald-100 text-emerald-600`;
      case 'PENDING':
      case 'SCHEDULED':
        return `${base} bg-sky-100 text-sky-600`;
      case 'DUE':
      case 'EXPIRED':
        return `${base} bg-amber-100 text-amber-600`;
      case 'CANCELLED':
      case 'FAILED':
        return `${base} bg-red-100 text-red-600`;
      default:
        return `${base} bg-amber-100 text-amber-600`;
    }
  }

  getInvoiceSerieFolio(invoice: InvoiceDTO): string {
    const serie = invoice.serie?.trim() ?? '';
    const folio = invoice.folio?.trim() ?? '';
    const joined = `${serie}${folio}`;
    return joined || '—';
  }

  getCfdiStatusLabel(status: string | undefined): string {
    switch (status) {
      case CfdiInvoiceStatusEnum.STAMPING:
        return 'Timbrando';
      case CfdiInvoiceStatusEnum.ACTIVE:
        return 'Vigente';
      case CfdiInvoiceStatusEnum.CANCELLATION_IN_PROGRESS:
        return 'En cancelación';
      case CfdiInvoiceStatusEnum.CANCELLED:
        return 'Cancelado';
      default:
        return status?.trim() ? status : '—';
    }
  }

  getCfdiStatusBadgeClasses(status: string | undefined): string {
    const base = 'text-[10px] px-1.5 py-0.5 rounded shrink-0';
    switch (status) {
      case CfdiInvoiceStatusEnum.STAMPING:
        return `${base} bg-sky-100 text-sky-700`;
      case CfdiInvoiceStatusEnum.ACTIVE:
        return `${base} bg-emerald-100 text-emerald-700`;
      case CfdiInvoiceStatusEnum.CANCELLATION_IN_PROGRESS:
        return `${base} bg-amber-100 text-amber-700`;
      case CfdiInvoiceStatusEnum.CANCELLED:
        return `${base} bg-red-100 text-red-700`;
      default:
        return `${base} bg-slate-100 text-slate-600`;
    }
  }

  getStatusIcon(status: SaleStatus | string | undefined): string {
    if (!status) return 'pi pi-receipt';
    switch (status) {
      case 'DRAFT':
        return 'pi pi-file-edit';
      case 'ISSUED':
        return 'pi pi-send';
      case 'PARTIALLY_PAID':
        return 'pi pi-wallet';
      case 'PAID':
        return 'pi pi-check-circle';
      case 'CANCELLED':
      case 'VOID':
        return 'pi pi-ban';
      case 'UNCOLLECTIBLE':
        return 'pi pi-exclamation-triangle';
      default:
        return 'pi pi-receipt';
    }
  }

  getStatusIconClasses(status: SaleStatus | string | undefined, compact = false): string {
    const size = compact ? 'h-7 w-7' : 'h-10 w-10';
    const base = `shrink-0 ${size} rounded-full flex items-center justify-center`;
    if (!status) return `${base} bg-slate-100 text-slate-600`;
    switch (status) {
      case 'DRAFT':
        return `${base} bg-sky-100 text-sky-600`;
      case 'ISSUED':
        return `${base} bg-violet-100 text-violet-600`;
      case 'PARTIALLY_PAID':
        return `${base} bg-amber-100 text-amber-600`;
      case 'PAID':
        return `${base} bg-emerald-100 text-emerald-600`;
      case 'CANCELLED':
      case 'VOID':
        return `${base} bg-slate-200 text-slate-600`;
      case 'UNCOLLECTIBLE':
        return `${base} bg-red-100 text-red-600`;
      default:
        return `${base} bg-slate-100 text-slate-600`;
    }
  }

  openCustomerDetails(): void {
    const customerId = this.sale()?.customer?.id;
    if (customerId != null) {
      this.openCustomer.emit(customerId);
    }
  }

  openSubscriptionDetails(): void {
    const subscriptionId = this.sale()?.subscription?.id;
    if (subscriptionId != null) {
      this.openSubscription.emit(subscriptionId);
    }
  }

  openChargeReceipt(charge: ChargeDTO): void {
    if (!charge?.id || this.isChargeReceiptLoading(charge)) return;
    this.chargeReceiptLoadingId.set(charge.id);
    this.invoiceService
      .createChargePublicHash(charge.id, 30)
      .pipe(finalize(() => this.chargeReceiptLoadingId.set(null)))
      .subscribe({
        next: (r) => {
          if (r.ok && r.body) {
            const baseUrl = window.location.origin;
            const url = `${baseUrl}/public/charges/${charge.id}/receipt?hash=${encodeURIComponent(r.body)}`;
            window.open(url, '_blank', 'noopener,noreferrer');
            this.toastr.success('Recibo público abierto en nueva pestaña');
          } else {
            this.toastr.error(r.message ?? 'No se pudo generar el hash del recibo');
          }
        },
        error: (err) => {
          this.toastr.error(err.error?.message ?? 'Error al generar el recibo');
        }
      });
  }

  isChargeReceiptLoading(charge: ChargeDTO): boolean {
    return charge.id != null && this.chargeReceiptLoadingId() === charge.id;
  }

  openCfdiPdfViewer(invoice: InvoiceDTO): void {
    const id = invoice.id;
    if (id == null || !invoice.pdfFileId || this.isInvoiceActionBusy(invoice)) return;
    this.previewLoadingInvoiceId.set(id);
    this.invoiceService
      .getStampPDF(id)
      .pipe(finalize(() => this.previewLoadingInvoiceId.set(null)))
      .subscribe({
        next: (r) => {
          if (r.ok && r.body?.content) {
            this.cfdiPdfViewerSafeUrl.set(this.stampPdfContentToSafeUrl(r.body.content));
            this.cfdiPdfViewerVisible.set(true);
          } else {
            this.toastr.error(apiFailureMessage(r, 'No se pudo obtener el PDF del CFDI'));
          }
        },
        error: (err) => {
          this.toastr.error(httpErrorApiMessage(err, 'Error al obtener el PDF'));
        }
      });
  }

  onCfdiPdfViewerVisibleChange(visible: boolean): void {
    this.cfdiPdfViewerVisible.set(visible);
    if (!visible) {
      this.cfdiPdfViewerSafeUrl.set(null);
    }
  }

  downloadStampPDF(invoice: InvoiceDTO): void {
    const id = invoice.id;
    if (id == null || !invoice.pdfFileId || this.isInvoiceActionBusy(invoice)) return;
    this.downloadPdfLoadingInvoiceId.set(id);
    this.invoiceService
      .downloadStampPdfFile(id)
      .pipe(finalize(() => this.downloadPdfLoadingInvoiceId.set(null)))
      .subscribe({
        next: ({ blob, filename }) => {
          const name = this.sanitizeDownloadFilename(filename, '.pdf');
          this.triggerBrowserDownload(blob, name);
          this.toastr.success('Descarga del PDF iniciada');
        },
        error: (err) => {
          this.toastr.error(httpErrorApiMessage(err, 'Error al descargar el PDF'));
        }
      });
  }

  downloadStampXML(invoice: InvoiceDTO): void {
    const id = invoice.id;
    if (id == null || !invoice.xmlFileId || this.isInvoiceActionBusy(invoice)) return;
    this.downloadXmlLoadingInvoiceId.set(id);
    this.invoiceService
      .downloadStampXmlFile(id)
      .pipe(finalize(() => this.downloadXmlLoadingInvoiceId.set(null)))
      .subscribe({
        next: ({ blob, filename }) => {
          const name = this.sanitizeDownloadFilename(filename, '.xml');
          this.triggerBrowserDownload(blob, name);
          this.toastr.success('Descarga del XML iniciada');
        },
        error: (err) => {
          this.toastr.error(httpErrorApiMessage(err, 'Error al descargar el XML'));
        }
      });
  }

  openInvoiceActionsMenu(event: Event, invoice: InvoiceDTO): void {
    event.stopPropagation();
    const pdfBusy = this.isDownloadPdfLoading(invoice);
    const xmlBusy = this.isDownloadXmlLoading(invoice);
    const resendBusy = this.resendCfdiLoading();
    this.invoiceRowMenuItems.set([
      {
        label: 'Descargar PDF',
        icon: 'pi pi-file-pdf',
        disabled: !invoice.pdfFileId || pdfBusy || xmlBusy || this.isPreviewLoading(invoice) || resendBusy,
        command: () => this.downloadStampPDF(invoice)
      },
      {
        label: 'Descargar XML',
        icon: 'pi pi-code',
        disabled: !invoice.xmlFileId || pdfBusy || xmlBusy || this.isPreviewLoading(invoice) || resendBusy,
        command: () => this.downloadStampXML(invoice)
      },
      {
        label: 'Reenviar CFDI',
        icon: 'pi pi-send',
        disabled: resendBusy || (!invoice.pdfFileId && !invoice.xmlFileId) || this.isPreviewLoading(invoice),
        command: () => this.openResendCfdiDialog(invoice)
      }
    ]);
    this.invoiceActionsMenu?.toggle(event);
  }

  openResendCfdiDialog(invoice: InvoiceDTO): void {
    if (invoice.id == null) return;
    this.resendCfdiInvoiceId.set(invoice.id);
    this.resendCfdiEmail.set(this.sale()?.customer?.email ?? invoice.customer?.email ?? '');
    this.resendCfdiDialogVisible.set(true);
  }

  closeResendCfdiDialog(): void {
    if (this.resendCfdiLoading()) return;
    this.resendCfdiDialogVisible.set(false);
    this.resendCfdiEmail.set('');
    this.resendCfdiInvoiceId.set(null);
  }

  submitResendCfdi(): void {
    const invoiceId = this.resendCfdiInvoiceId();
    const email = this.resendCfdiEmail().trim();
    if (invoiceId == null) return;
    if (!email) {
      this.toastr.error('Indica el correo electrónico al que se enviará el CFDI.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.toastr.error('El correo electrónico no es válido.');
      return;
    }
    this.resendCfdiLoading.set(true);
    this.invoiceService
      .resendCfdi(invoiceId, email)
      .pipe(finalize(() => this.resendCfdiLoading.set(false)))
      .subscribe({
        next: (r) => {
          if (r.ok) {
            this.toastr.success(r.message ?? 'CFDI reenviado correctamente');
            this.resendCfdiDialogVisible.set(false);
            this.resendCfdiEmail.set('');
            this.resendCfdiInvoiceId.set(null);
          } else {
            this.toastr.error(apiFailureMessage(r, 'No se pudo reenviar el CFDI'));
          }
        },
        error: (err) => {
          this.toastr.error(httpErrorApiMessage(err, 'Error al reenviar el CFDI'));
        }
      });
  }

  isPreviewLoading(invoice: InvoiceDTO): boolean {
    return invoice.id != null && this.previewLoadingInvoiceId() === invoice.id;
  }

  isDownloadPdfLoading(invoice: InvoiceDTO): boolean {
    return invoice.id != null && this.downloadPdfLoadingInvoiceId() === invoice.id;
  }

  isDownloadXmlLoading(invoice: InvoiceDTO): boolean {
    return invoice.id != null && this.downloadXmlLoadingInvoiceId() === invoice.id;
  }

  isInvoiceActionBusy(invoice: InvoiceDTO): boolean {
    return (
      this.isPreviewLoading(invoice) ||
      this.isDownloadPdfLoading(invoice) ||
      this.isDownloadXmlLoading(invoice) ||
      this.resendCfdiLoading()
    );
  }

  isInvoiceMenuLoading(invoice: InvoiceDTO): boolean {
    return this.isDownloadPdfLoading(invoice) || this.isDownloadXmlLoading(invoice) || this.resendCfdiLoading();
  }

  private loadInvoices(saleId: number): void {
    this.invoicesLoading.set(true);
    this.invoiceService
      .listInvoices({ saleId }, { page: 0, size: 100, sort: ['issueDate,desc'] })
      .pipe(finalize(() => this.invoicesLoading.set(false)))
      .subscribe({
        next: (r) => this.invoices.set(r.ok && r.body ? r.body : []),
        error: () => this.invoices.set([])
      });
  }

  private normalizeStampPdfSrc(content: string): string {
    const t = content.trim();
    if (t.startsWith('http://') || t.startsWith('https://') || t.startsWith('blob:') || t.startsWith('data:')) {
      return t;
    }
    return `data:application/pdf;base64,${t}`;
  }

  private stampPdfContentToSafeUrl(content: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(this.normalizeStampPdfSrc(content));
  }

  private sanitizeDownloadFilename(name: string, defaultExt: string): string {
    let s = name.replace(/[/\\?%*:|"<>]/g, '_').trim() || `CFDI${defaultExt}`;
    if (!/\.\w{2,8}$/i.test(s)) {
      s += defaultExt.startsWith('.') ? defaultExt : `.${defaultExt}`;
    }
    return s;
  }

  private triggerBrowserDownload(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}
